import { FetcherPaginated, FetchParams } from "@shared/types";
import { CommentType } from "./CommentTypes";
import { fetcherGetPostComments } from "../../DataFetchers/CommentDataFetcher";
import { RequestCriteriaType } from "@infrastructure/Services/PostService.types";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { ROOT_MARGIN } from "../Post/PostsWrapper";
import { useCursoredInfiniteScroll } from "@infrastructure/Hooks/InfiniteScroll/useCursoredInfiniteScroll";

export const getCommentsFetcher: FetcherPaginated<CommentType> = ({
    postId,
    sortCriteria,
}: FetchParams) =>
    fetcherGetPostComments<CommentType, CommentType["id"]>(
        postId,
        sortCriteria ?? {},
    );

const noopFetcher: FetcherPaginated<CommentType> = () => {
    return Promise.resolve([]);
};

export const useCommentsWithInfiniteScroll = ({
    postId,
    sortCriteria,
    scrollableContainerRef,
}: {
    postId: number | undefined;
    sortCriteria: RequestCriteriaType;
    scrollableContainerRef?: MutableRefObject<HTMLDivElement>;
}) => {
    const [listOptions, setListOptions] = useState({
        fetcher: noopFetcher,
        fetchParams: {
            postId,
            sortCriteria,
        },
        maxItemCount: 5000,
    });
    const {
        updateItem,
        loading,
        items: comments,
        hasNextPage,
        error,
        loadMore,
        isInitDone,
        deleteItem,
        resetParams,
        insertItemAtIndex,
    } = useCursoredInfiniteScroll(
        listOptions.fetcher,
        listOptions.fetchParams,
        {},
        listOptions.maxItemCount,
    );

    const [infiniteRef, { rootRef }] = useInfiniteScroll({
        loading,
        hasNextPage,
        onLoadMore: loadMore,
        disabled: !!error,
        rootMargin: ROOT_MARGIN.SEMI_LAZY,
    });

    useEffect(() => {
        if (scrollableContainerRef?.current) {
            rootRef(scrollableContainerRef.current);
        }
    }, [scrollableContainerRef?.current, rootRef]);

    useEffect(() => {
        if (!postId) {
            return;
        }

        setListOptions((prevState) => ({
            ...prevState,
            fetcher: getCommentsFetcher,
            fetchParams: {
                postId,
                sortCriteria,
            },
        }));

        return () => {
            setListOptions((prevState) => ({
                ...prevState,
                fetcher: noopFetcher,
                fetchParams: {
                    postId,
                    sortCriteria,
                },
            }));
            batchesLoadedRef.current = 0;
        };
    }, [postId, sortCriteria]);

    useEffect(() => {
        resetParams(listOptions.fetchParams);
    }, [listOptions.fetchParams]);

    const batchesLoadedRef = useRef(0);
    useEffect(() => {
        if (!isInitDone) {
            return;
        }
        batchesLoadedRef.current = batchesLoadedRef.current + 1;
    }, [isInitDone]);

    function reInitiate() {
        resetParams({
            postId,
            sortCriteria,
        });
        batchesLoadedRef.current = 0;
    }

    return {
        rootRef,
        infiniteRef,
        loadMore,
        loading,
        isInitDone,
        hasNextPage,
        error,
        updateComment: updateItem,
        deleteComment: deleteItem,
        batchesLoaded: batchesLoadedRef.current,
        comments,
        insertItemAtIndex,
        reInitiate,
    };
};
